import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, Trash2, CreditCard, Shield, Settings } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const dropdownRef = useRef(null);
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications
  } = useNotifications();

  // Sound for new notifications - Apple-style notification sound
  const playNotificationSound = () => {
    // Create a nice, clean notification sound similar to iOS/macOS
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    const playTone = (frequency, duration, volume = 0.1) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    try {
      // Play two quick tones like Apple notification (C and E notes)
      playTone(523.25, 0.15, 0.08); // C5
      setTimeout(() => playTone(659.25, 0.15, 0.06), 50); // E5
    } catch (error) {
      // Fallback for browsers that don't support Web Audio API
    }
  };

  // Previous unread count to detect new notifications
  const prevUnreadCount = useRef(unreadCount);

  useEffect(() => {
    if (unreadCount > prevUnreadCount.current && prevUnreadCount.current !== null) {
      playNotificationSound();
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get icon for notification type
  const getNotificationIcon = (notification) => {
    const iconProps = { className: "w-3.5 h-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" };
    const category = notification.metadata?.category || notification.category;

    switch (category) {
      case 'billing':
        return <CreditCard {...iconProps} />;
      case 'subscription':
        return <Shield {...iconProps} />;
      case 'system':
        return <Settings {...iconProps} />;
      default:
        return <Bell {...iconProps} />;
    }
  };

  // Get notification indicator color based on type
  const getNotificationIndicator = (type) => {
    switch (type) {
      case 'success':
        return 'bg-green-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-blue-500';
    }
  };

  // Format notification time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // Handle notification click - open modal
  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    setSelectedNotification(notification);
    setIsOpen(false);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  // Handle delete notification
  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    await deleteNotification(notificationId);
  };

  // Handle delete all notifications
  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      const success = await deleteAllNotifications();
      if (success) {
        setShowDeleteAllModal(false);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setIsDeleting(false);
    }
  };

  // Recent notifications (last 10)
  const recentNotifications = notifications.slice(0, 10);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-blue-500 text-white text-[10px] rounded-full min-w-[16px] h-4 flex items-center justify-center font-medium px-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="fixed sm:absolute right-4 sm:right-0 left-4 sm:left-auto top-16 sm:top-auto mt-0 sm:mt-2 sm:w-96 bg-white/80 dark:bg-[#111]/90 backdrop-blur-xl border border-gray-200 dark:border-white/[0.1] rounded-[16px] shadow-2xl z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-white/[0.06] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {unreadCount} new
                </span>
              )}
            </div>
            {notifications.length > 0 && (
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="px-3 py-1.5 text-xs font-medium text-blue-500 bg-blue-500/[0.08] hover:bg-blue-500/[0.12] rounded-full transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setShowDeleteAllModal(true)}
                  className="px-3 py-1.5 text-xs font-medium text-red-500 bg-red-500/[0.08] hover:bg-red-500/[0.12] rounded-full transition-colors"
                >
                  Delete all
                </button>
              </div>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Loading notifications...
              </div>
            ) : recentNotifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-700" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1">You'll see important updates here</p>
              </div>
            ) : (
              recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className="relative group border-b border-gray-100 dark:border-white/[0.04] last:border-b-0"
                >
                  <div
                    onClick={() => handleNotificationClick(notification)}
                    className="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className={`text-sm ${!notification.is_read ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'} truncate`}>
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getNotificationIndicator(notification.type)}`}></div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                      {/* Delete Button */}
                      <button
                        onClick={(e) => handleDeleteNotification(e, notification.id)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete notification"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      )}

      {/* Notification Detail Modal - Rendered via Portal */}
      {selectedNotification && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelectedNotification(null)}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-[#111] rounded-[20px] border border-gray-200 dark:border-white/[0.06] shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-white/[0.06]">
              <div className="flex items-center space-x-3">
                {getNotificationIcon(selectedNotification)}
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedNotification.title}
                </h2>
              </div>
              <button
                onClick={() => setSelectedNotification(null)}
                className="p-2 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 font-light leading-relaxed">
                {selectedNotification.message}
              </p>
              <div className="mt-4 text-xs text-gray-500 dark:text-gray-500">
                {formatTime(selectedNotification.created_at)}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowDeleteAllModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-[#111] rounded-[20px] border border-gray-200 dark:border-white/[0.06] shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-white/[0.06]">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete All Notifications
              </h2>
              <button
                onClick={() => setShowDeleteAllModal(false)}
                className="p-2 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Are you sure you want to delete all {notifications.length} notification{notifications.length !== 1 ? 's' : ''}? This action cannot be undone.
              </p>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowDeleteAllModal(false)}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-full hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAll}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete All'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};


export default NotificationBell;
